import React from 'react';
import { motion } from 'framer-motion';
import { User, MapPin, Calendar, Mail, Phone } from 'lucide-react';

const About: React.FC = () => {
  const personalInfo = [
    { icon: <User size={20} />, label: 'Name', value: 'Samir Ahmed Talkal' },
    { icon: <MapPin size={20} />, label: 'Location', value: 'Bengaluru, India' },
    { icon: <Calendar size={20} />, label: 'Experience', value: '3+ Years' },
    { icon: <Mail size={20} />, label: 'Email', value: 'samirtalkal@gmail.com' },
    { icon: <Phone size={20} />, label: 'Phone', value: '+91-8805085163' },
  ];

  return (
    <section id="about" className="section-padding bg-gray-50 dark:bg-dark-800">
      <div className="container-max">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
            About Me
          </h2>
          <p className="text-lg text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
            An AI Engineer specializing in enterprise-grade agents, LLMs, and RAG pipelines
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Column - Personal Info */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="space-y-8"
          >
            <div>
              <h3 className="text-2xl font-semibold text-gray-900 dark:text-white mb-6">
                Personal Information
              </h3>
              <div className="space-y-4">
                {personalInfo.map((info, index) => (
                  <div key={index} className="flex items-center space-x-4">
                    <div className="flex-shrink-0 w-10 h-10 bg-primary-100 dark:bg-primary-900 rounded-lg flex items-center justify-center text-primary-600 dark:text-primary-400">
                      {info.icon}
                    </div>
                    <div>
                      <p className="text-sm text-gray-500 dark:text-gray-400 font-medium">{info.label}</p>
                      <p className="text-gray-900 dark:text-white">{info.value}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <h3 className="text-2xl font-semibold text-gray-900 dark:text-white mb-6">
                Education
              </h3>
              <div className="space-y-4">
                <div className="bg-white dark:bg-dark-700 p-4 rounded-lg shadow-sm border border-gray-100 dark:border-dark-600">
                  <p className="text-lg font-semibold text-primary-600 dark:text-primary-400">New York University</p>
                  <p className="text-sm text-gray-600 dark:text-gray-300">Master of Science - Computer Engineering</p>
                  <p className="text-sm text-gray-500">GPA: 3.83/4.0 | 2022 - 2024</p>
                </div>
                <div className="bg-white dark:bg-dark-700 p-4 rounded-lg shadow-sm border border-gray-100 dark:border-dark-600">
                  <p className="text-lg font-semibold text-primary-600 dark:text-primary-400">Vellore Institute of Technology</p>
                  <p className="text-sm text-gray-600 dark:text-gray-300">Bachelor of Technology - Information Technology</p>
                  <p className="text-sm text-gray-500">GPA: 8.62/10 | 2016 - 2020</p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Right Column - About Text */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="space-y-6"
          >
            <div>
              <h3 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
                My Journey
              </h3>
              <div className="space-y-4 text-gray-600 dark:text-gray-300">
                <p>
                  I'm an AI Engineer with 3+ years of experience in AI and software development, specializing
                  in enterprise-grade AI agents, LLMs, RAG pipelines, and conversational AI systems. With a
                  Master's in Computer Engineering from NYU, I bring a strong foundation in both engineering
                  rigor and applied machine learning.
                </p>
                <p>
                  My expertise spans Python, FastAPI, LangChain, and LangGraph, with hands-on experience in
                  agent evaluation, LLM observability, and delivering production-ready AI solutions. I've worked
                  with vector stores like ChromaDB, Qdrant, and FAISS, and I'm comfortable across the full stack
                  from prompt engineering to cloud deployment.
                </p>
                <p>
                  Currently leading AI engineering initiatives at EY-GDS, I architect and deliver enterprise-grade
                  AI agents end-to-end, own the product lifecycle from stakeholder requirements to deployment, and
                  implement observability with tools like Langfuse to continuously evaluate and improve agent
                  performance in production.
                </p>
              </div>
            </div>

            <div>
              <h3 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
                What I Specialize In
              </h3>
              <div className="space-y-3">
                <div className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-primary-600 dark:bg-primary-400 rounded-full mt-2 flex-shrink-0"></div>
                  <p className="text-gray-600 dark:text-gray-300">Enterprise-grade AI agents and agentic systems with LangChain &amp; LangGraph</p>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-primary-600 dark:bg-primary-400 rounded-full mt-2 flex-shrink-0"></div>
                  <p className="text-gray-600 dark:text-gray-300">RAG pipelines, vector search, and document intelligence</p>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-primary-600 dark:bg-primary-400 rounded-full mt-2 flex-shrink-0"></div>
                  <p className="text-gray-600 dark:text-gray-300">LLM observability, agent evaluation, and production monitoring</p>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-primary-600 dark:bg-primary-400 rounded-full mt-2 flex-shrink-0"></div>
                  <p className="text-gray-600 dark:text-gray-300">Async FastAPI microservices and scalable cloud architecture</p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default About; 